/**
 * Client-side branded score card (PNG via Canvas). Used as a fallback when
 * navigator.canShare({files}) isn't supported or when we want a downloadable
 * copy. Primary share path is the backend-rendered OG card at /api/share/.
 */
import { api } from "../lib/api";
import { toast } from "sonner";

function drawBrandedBg(ctx, W, H) {
  ctx.fillStyle = "#0f0f1e";
  ctx.fillRect(0, 0, W, H);
  const gg = ctx.createRadialGradient(W / 2, 540, 60, W / 2, 540, 560);
  gg.addColorStop(0, "rgba(46,204,113,0.45)");
  gg.addColorStop(1, "rgba(46,204,113,0)");
  ctx.fillStyle = gg;
  ctx.fillRect(0, 0, W, H);

  ctx.strokeStyle = "rgba(46,117,182,0.18)";
  ctx.lineWidth = 1;
  for (let x = 0; x < W; x += 48) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, H);
    ctx.stroke();
  }
  for (let y = 0; y < H; y += 48) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(W, y);
    ctx.stroke();
  }
  ctx.fillStyle = "rgba(0,0,0,0.18)";
  for (let y = 0; y < H; y += 3) ctx.fillRect(0, y, W, 1);

  ctx.strokeStyle = "#2ecc71";
  ctx.lineWidth = 6;
  ctx.shadowColor = "rgba(46,204,113,0.85)";
  ctx.shadowBlur = 22;
  ctx.strokeRect(30, 30, W - 60, H - 60);
  ctx.shadowBlur = 0;
}

function drawTitles(ctx, W) {
  ctx.textAlign = "center";
  ctx.font = "bold 72px 'Press Start 2P', monospace";
  ctx.fillStyle = "#2ecc71";
  ctx.shadowColor = "rgba(46,204,113,0.9)";
  ctx.shadowBlur = 24;
  ctx.fillText("CRYPTO", W / 2, 180);
  ctx.fillText("BOOM", W / 2, 270);
  ctx.shadowBlur = 0;
}

function drawMascotHalo(ctx, cx, cy, r, brandImg) {
  const halo = ctx.createRadialGradient(cx, cy, 20, cx, cy, r * 1.8);
  halo.addColorStop(0, "rgba(46,204,113,0.9)");
  halo.addColorStop(0.6, "rgba(46,204,113,0.2)");
  halo.addColorStop(1, "rgba(46,204,113,0)");
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(cx, cy, r * 1.7, 0, Math.PI * 2);
  ctx.fill();
  if (brandImg && brandImg.complete && brandImg.naturalWidth > 0) {
    const spriteSize = r * 2.4;
    ctx.drawImage(brandImg, cx - spriteSize / 2, cy - spriteSize / 2, spriteSize, spriteSize);
  } else {
    ctx.fillStyle = "#2ecc71";
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawStats(ctx, W, result) {
  ctx.textAlign = "center";
  ctx.fillStyle = "#F7931A";
  ctx.shadowColor = "rgba(247,147,26,0.9)";
  ctx.shadowBlur = 30;
  ctx.font = "bold 140px 'Press Start 2P', monospace";
  ctx.fillText(String(result.score), W / 2, 880);
  ctx.shadowBlur = 0;

  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = "24px 'JetBrains Mono', monospace";
  ctx.fillText("SCORE", W / 2, 920);

  const row = (label, value, x, color) => {
    ctx.fillStyle = color;
    ctx.font = "bold 44px 'Press Start 2P', monospace";
    ctx.fillText(value, x, 1020);
    ctx.fillStyle = "rgba(255,255,255,0.55)";
    ctx.font = "20px 'JetBrains Mono', monospace";
    ctx.fillText(label, x, 1058);
  };
  row("DISTANCE", `${result.distance}m`, 260, "#ffffff");
  row("BLOCKS", String(result.blocks), 540, "#2ecc71");
  row("BC", `+${result.bc_earned}`, 820, "#F7931A");
}

function drawCTAAndRef(ctx, W, H, refCode) {
  ctx.textAlign = "center";
  ctx.fillStyle = "#2ecc71";
  ctx.shadowColor = "rgba(46,204,113,0.8)";
  ctx.shadowBlur = 18;
  ctx.font = "bold 36px 'Press Start 2P', monospace";
  ctx.fillText("CAN YOU BEAT ME?", W / 2, 1160);
  ctx.shadowBlur = 0;

  ctx.fillStyle = "#ffffff";
  ctx.font = "24px 'JetBrains Mono', monospace";
  const origin = window.location.origin.replace(/^https?:\/\//, "");
  ctx.fillText(refCode ? `${origin}/register?ref=${refCode}` : origin, W / 2, 1220);
  if (refCode) {
    ctx.fillStyle = "#F7931A";
    ctx.font = "bold 22px 'Press Start 2P', monospace";
    ctx.fillText(`CODE · ${refCode}`, W / 2, 1270);
  }

  ctx.fillStyle = "rgba(255,255,255,0.4)";
  ctx.font = "18px 'JetBrains Mono', monospace";
  ctx.fillText("UNI2CHAIN · PLAY TO EARN", W / 2, 1310);
}

export async function buildScoreCardBlob({ result, user, brandImg }) {
  const W = 1080;
  const H = 1350;
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d");
  drawBrandedBg(ctx, W, H);
  drawTitles(ctx, W);
  drawMascotHalo(ctx, W / 2, 560, 220, brandImg);
  drawStats(ctx, W, result);
  drawCTAAndRef(ctx, W, H, user?.referral_code);
  return new Promise((resolve) => c.toBlob((blob) => resolve(blob), "image/png", 0.92));
}

/** Orchestrates the share flow + +BC reward (skipped if user cancels). */
export async function shareScore({ result, user, brandImg, refreshUser, backendUrl }) {
  if (!result) return;
  const ref = user?.referral_code;
  const link = ref ? `${backendUrl}/api/share/${ref}` : window.location.origin;
  const text = `I just scored ${result.score} on CRYPTOBOOM and earned ${result.bc_earned} BC. Beat me: ${link}`;

  let shared = false;
  try {
    const blob = await buildScoreCardBlob({ result, user, brandImg });
    const file = new File([blob], "cryptoboom-score.png", { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
      await navigator.share({ title: "CRYPTOBOOM", text, files: [file] });
      shared = true;
    } else {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "cryptoboom-score.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      await navigator.clipboard.writeText(text);
      toast.success("Card saved + text copied — paste & share!");
      shared = true;
    }
  } catch (err) {
    if (err?.name === "AbortError") return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Score copied — paste & share!");
      shared = true;
    } catch (copyErr) {
      console.warn("clipboard copy failed:", copyErr?.message || copyErr);
    }
  }
  if (!shared) return;
  try {
    const { data } = await api.post("/social/verify/share-score", {});
    toast.success(`+${data.bc_earned} BC for sharing!`);
    await refreshUser();
  } catch (rewardErr) {
    // Daily cap / cooldown is expected.
    console.debug("share reward skipped:", rewardErr?.response?.status || rewardErr);
  }
}
